<template>
  <v-autocomplete
    :value="value"
    :items="items"
    :loading="loading"
    :search-input.sync="query"
    :label="label"
    :hint="hint"
    persistent-hint
    item-text="label"
    item-value="id"
    :no-data-text="noDataText"
    :no-filter="true"
    clearable
    return-object
    @change="$emit('input', $event)"
  ></v-autocomplete>
</template>

<script>
import { Region, errorHandler } from 'aveine-toolbox'

/**
 * Selection d'une region par recherche sur son nom (REDMINE-13656), sur le
 * modele de AveineWinePicker.
 *
 * Le libelle porte le pays : plusieurs regions ont le meme nom d'un pays a
 * l'autre (« Moselle » en Allemagne et au Luxembourg).
 */
export default {
  name: 'AveineRegionPicker',
  props: {
    /** La region choisie ({ id, label }), ou null. */
    value: { type: Object, default: null },
    label: { type: String, required: true },
    hint: { type: String, default: '' },
    APIbaseURL: String
  },
  data: () => ({
    entries: [],
    loading: false,
    query: null,
    debounce: null
  }),
  computed: {
    /** La region deja choisie, injectee pour que le champ ne paraisse pas vide. */
    items () {
      const entries = this.entries.slice()
      if (this.value && !entries.some(entry => entry.id === this.value.id)) {
        entries.unshift(this.value)
      }
      return entries
    },
    noDataText () {
      return this.query && this.query.length >= 2
        ? this.$t('intranet.homeContent.featuredRegion.noRegion')
        : this.$t('intranet.homeContent.picker.typeToSearch')
    }
  },
  watch: {
    query (value) {
      if (!value || value.length < 2) return
      clearTimeout(this.debounce)
      this.debounce = setTimeout(() => this.search(value), 300)
    }
  },
  beforeDestroy () {
    clearTimeout(this.debounce)
  },
  methods: {
    async search (value) {
      this.loading = true
      try {
        // `=ilike=` sur le francais OU l'anglais : voir AveineWinePicker pour
        // les operateurs que creddy connait, et l'echappement des jokers.
        const term = String(value).replace(/[%_\\]/g, m => '\\' + m)
        const response = await Region.api().get(
          `regions?lang=${this.$i18n.locale}`,
          {
            baseURL: this.APIbaseURL,
            params: {
              include: 'country',
              filter: `name.fr=ilike=%${term}%,name.en=ilike=%${term}%`,
              'page[size]': 20
            }
          }
        )
        this.entries = (response.entities ? response.entities.regions || [] : []).map(region => {
          const full = Region.query().whereId(region.id).with('country').first() || region
          return { id: region.id, label: this.$options.describe(full, this.$i18n.locale) }
        })
      } catch (errors) {
        errorHandler(errors, this)
      } finally {
        this.loading = false
      }
    }
  },

  /** « Moselle (Allemagne) ». Partage avec la liste des regions du profil. */
  describe (region, locale) {
    const name = translate(region && region.name, locale)
    const country = translate(region && region.country && region.country.name, locale)
    return country ? `${name} (${country})` : name
  }
}

function translate (translation, locale) {
  if (!translation) return ''
  if (typeof translation === 'string') return translation
  return translation[locale] || translation.fr || translation.en || ''
}
</script>
